import type {CodeNavigationInfo, CodeSection, CodeSectionLookup, DefinitionOrReference} from '@github-ui/code-nav'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import type {PanelType} from '@github-ui/code-view-types'
import {dynamic} from '@github-ui/dynamic'
import {hpcAttrs} from '@github-ui/hpc'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {GlobalCommands} from '@github-ui/ui-commands'
import {Link} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useEffect} from 'react'

import {useDeferredRichText} from '../../../contexts/DeferredRichTextContext'
import {useSetFindInFileOpen} from '../../../contexts/FindInFileOpenContext'
import {useSetSearchTerm} from '../../../contexts/SearchTermContext'
import {useCurrentBlame} from '../../../hooks/CurrentBlame'
import {useCurrentBlob} from '../../../hooks/CurrentBlob'
import {useCurrentStyledBlob} from '../../../hooks/CurrentStyledBlob'
import {BlobDisplayType, useBlobRendererType} from '../../../hooks/use-blob-renderer-type'
import {useIsCursorEnabled} from '../../../hooks/use-cursor-navigation'
import type {SetStickyLinesType} from '../../../hooks/use-sticky-lines'
import {assertNever} from '../../../utilities/assert-never'
import {MarkdownContent} from '../../MarkdownContent'
import {LoadingFallback} from '../../SuspenseFallback'
import styles from './BlobContent.module.css'
import type {CodeLinesHandle} from './Code/code-lines-handle'
import {CodeBlob} from './Code/CodeBlob'
import {ImageBlob} from './Renderable/ImageBlob'
import {YamlTemplateContent, YamlTemplateType} from './Renderable/YamlTemplateContent'

const CSVBlob = dynamic(() => import('./CSV/CSVBlob'))
const FileRendererBlob = dynamic(() => import('@github-ui/file-renderer-blob'))

export interface CodeNavData {
  selectedText: string
  lineNumber: number
  offset: number
}

export function BlobContent({
  setOpenPanel,
  codeNavInfo,
  validCodeNav,
  onCodeNavTokenSelected,
  onLineStickOrUnstick,
  searchResults,
  blobLinesHandle,
  focusedSearchResult,
}: {
  setOpenPanel: (panel: PanelType | undefined) => void
  codeNavInfo: CodeNavigationInfo | undefined
  validCodeNav: boolean
  onCodeNavTokenSelected: (codeNavData: CodeNavData) => void
  onLineStickOrUnstick: SetStickyLinesType
  searchResults: DefinitionOrReference[]
  blobLinesHandle: React.RefObject<CodeLinesHandle | null>
  focusedSearchResult: number | undefined
}) {
  const hasBlame = !!useCurrentBlame()
  const {rawLines} = useCurrentStyledBlob()
  const {sendRepoKeyDownEvent} = useReposAnalytics()
  const setSearchTerm = useSetSearchTerm()

  const blobRendererType = useBlobRendererType()
  const setFindInFileOpen = useSetFindInFileOpen()
  const isRenderingCode = blobRendererType === BlobDisplayType.Code

  // Find in file
  const findInFileHotkeyEnabled = isRenderingCode && rawLines != null && validCodeNav
  const cursorEnabled = useIsCursorEnabled()

  useEffect(() => {
    if (!isRenderingCode) {
      setOpenPanel(undefined)
    }
  }, [isRenderingCode, setOpenPanel])

  function onHotkeyPressed() {
    setFindInFileOpen(true)
    sendRepoKeyDownEvent('BLOB_FIND_IN_FILE_MENU.OPEN')
    const selection = window.getSelection()?.toString()
    if (selection) {
      setSearchTerm(selection)
    }
  }
  // We can't have the overflow classes for these types because it interferes with the HighlightedLineMenu
  // For markdown, overflow: auto causes lag with code blocks
  const hasOverflow =
    !isRenderingCode && blobRendererType !== BlobDisplayType.CSV && blobRendererType !== BlobDisplayType.Markdown
  const isMarkdown = blobRendererType === BlobDisplayType.Markdown

  return (
    <section
      aria-labelledby="file-name-id-wide file-name-id-mobile"
      className={clsx(
        styles.blobContentSection,
        isMarkdown && styles.blobContentSectionMarkdown,
        hasOverflow && styles.blobContentSectionOverflow,
      )}
      style={{
        // 46px is the height of the header, and to fix the weirdness of the header pushing all content down, we need
        // to add padding to the body content of a given view. Otherwise the header covers up the first few lines of
        // any given view. 92px is the height of the blame header because there is an extra line with the color scheme
        marginTop: hasBlame ? '92px' : '46px',
      }}
    >
      <Blob
        blobLinesHandle={blobLinesHandle}
        onCodeNavTokenSelected={onCodeNavTokenSelected}
        codeSections={hasBlame ? undefined : codeNavInfo?.codeSections}
        codeLineToSectionMap={codeNavInfo ? codeNavInfo.lineToSectionMap : undefined}
        validCodeNav={validCodeNav}
        onLineStickOrUnstick={onLineStickOrUnstick}
        searchResults={searchResults}
        focusedSearchResult={focusedSearchResult}
      />
      {isRenderingCode && !cursorEnabled && findInFileHotkeyEnabled && (
        <GlobalCommands commands={{'code-view:find-in-file': onHotkeyPressed}} />
      )}
    </section>
  )
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const Blob = React.memo(function Blob({
  onCodeNavTokenSelected,
  codeSections,
  codeLineToSectionMap,
  validCodeNav,
  onLineStickOrUnstick,
  searchResults,
  blobLinesHandle,
  focusedSearchResult,
}: {
  onCodeNavTokenSelected: (codeNavData: CodeNavData) => void
  codeSections?: Map<number, CodeSection[]>
  codeLineToSectionMap?: CodeSectionLookup
  validCodeNav: boolean
  onLineStickOrUnstick: SetStickyLinesType
  searchResults: DefinitionOrReference[]
  blobLinesHandle: React.RefObject<CodeLinesHandle | null>
  focusedSearchResult: number | undefined
}) {
  const payload = useCurrentBlob()

  const displayType = useBlobRendererType()

  switch (displayType) {
    case BlobDisplayType.TooLargeError:
      return (
        <div className={styles.tooLargeError} {...hpcAttrs()}>
          <Link href={payload.rawBlobUrl}>View raw</Link>
          {payload.large && <p>(Sorry about that, but we can’t show files that are this big right now.)</p>}
        </div>
      )
    case BlobDisplayType.Code:
      return (
        <Suspense fallback={<LoadingFallback />}>
          <CodeBlob
            blobLinesHandle={blobLinesHandle}
            onCodeNavTokenSelected={onCodeNavTokenSelected}
            codeSections={codeSections}
            codeLineToSectionMap={codeLineToSectionMap}
            validCodeNav={validCodeNav}
            onLineStickOrUnstick={onLineStickOrUnstick}
            searchResults={searchResults}
            focusedSearchResult={focusedSearchResult}
          />
        </Suspense>
      )
    case BlobDisplayType.Markdown:
      return (
        <ErrorBoundary boundaryName="markdown-blob">
          <DeferredMarkdownContent className={styles.markdownBlob} />
        </ErrorBoundary>
      )
    case BlobDisplayType.CSV:
      return (
        <ErrorBoundary boundaryName="csv-blob">
          <Suspense fallback={<LoadingFallback />}>
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            <CSVBlob csv={payload.csv!} />
          </Suspense>
        </ErrorBoundary>
      )
    case BlobDisplayType.FileRenderer:
      return (
        <ErrorBoundary boundaryName="file-renderer-blob">
          <Suspense fallback={<LoadingFallback />}>
            <FileRendererBlob
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              key={payload.renderedFileInfo!.identityUUID}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              identityUuid={payload.renderedFileInfo!.identityUUID}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              size={payload.renderedFileInfo!.size}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              type={payload.renderedFileInfo!.renderFileType}
              url={payload.displayUrl}
            />
          </Suspense>
        </ErrorBoundary>
      )
    case BlobDisplayType.Image:
      return (
        <ErrorBoundary boundaryName="image-blob">
          <ImageBlob displayName={payload.displayName} displayUrl={payload.displayUrl} />
        </ErrorBoundary>
      )
    case BlobDisplayType.IssueTemplate:
      return (
        <ErrorBoundary boundaryName="issue-template-blob">
          <YamlTemplateContent
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            issueTemplate={payload.issueTemplate ? payload.issueTemplate : payload.discussionTemplate!}
            type={payload.issueTemplate ? YamlTemplateType.Issue : YamlTemplateType.Discussion}
          />
        </ErrorBoundary>
      )
    default:
      assertNever(displayType)
  }
})

function DeferredMarkdownContent({className}: {className?: string}) {
  const payload = useCurrentBlob()
  const {richText: deferredRichText} = useDeferredRichText()

  const richText = payload.richText ?? deferredRichText

  if (!richText) {
    return <LoadingFallback />
  }

  return <MarkdownContent richText={richText} className={className} />
}
